import { useRef, useState } from 'react';

import { ApiError } from 'src/api/client';
import { useUploadSpeakerClip } from 'src/api/queries';

/** What the API accepts. Everything else is refused here rather than three hours later on the box. */
const ACCEPTED_FORMATS = ['wav', 'mp3', 'm4a', 'aac', 'ogg', 'webm', 'flac'];

/**
 * The upload is base64 in a JSON body, so the practical ceiling is the encoded size. A minute of
 * speech is well under this; a whole broadcast is not, and it is not a voice sample either.
 */
const MAX_FILE_BYTES = 5 * 1024 * 1024;

type UploadClipsProps = {
  profileId: string;
  /** Shown when the profile itself is still queued — the clips will land after it exists. */
  note?: string;
};

/**
 * Feeds a voice audio an editor already has.
 *
 * The system learns a voice by hearing it, and waiting for it to appear in a broadcast means the
 * first broadcast it appears in is labeled wrong. Thirty seconds of a press conference from last
 * month fixes that in advance.
 *
 * Files are read and sent one at a time, deliberately: each becomes its own queued command, so a
 * file the box cannot decode fails alone and says so, instead of taking the batch with it.
 */
export function UploadClips({ profileId, note }: UploadClipsProps) {
  const upload = useUploadSpeakerClip();
  const inputRef = useRef<HTMLInputElement>(null);
  const [label, setLabel] = useState('');
  const [queued, setQueued] = useState(0);
  const [rejected, setRejected] = useState<string[]>([]);

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;

    setQueued(0);
    setRejected([]);

    for (const file of Array.from(files)) {
      const format = file.name.split('.').pop()?.toLowerCase() ?? '';

      if (!ACCEPTED_FORMATS.includes(format)) {
        setRejected(current => [...current, `${file.name}: formato no aceptado`]);
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        setRejected(current => [...current, `${file.name}: pesa más de 5 MB`]);
        continue;
      }

      try {
        const audioBase64 = await readAsBase64(file);
        await upload.mutateAsync({ profileId, audioBase64, format, label: label.trim() });
        setQueued(current => current + 1);
      } catch (error) {
        setRejected(current => [...current, `${file.name}: ${error instanceof ApiError ? error.message : 'no se pudo subir'}`]);
      }
    }

    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <section className="upload-clips">
      <h3 className="section-title">Subir audio de esta voz</h3>
      <p className="continue__note">
        Sube uno o varios archivos con la persona hablando sola: entre 10 segundos y 2 minutos por archivo, sin música
        ni otras voces encima. El sistema saca la huella de voz de cada uno en su siguiente corrida.
        {note ? ` ${note}` : ''}
      </p>

      <label className="process-form__field">
        <span>Qué se escucha (opcional)</span>
        <input
          type="text"
          value={label}
          placeholder="Conferencia del 12 de agosto, respuesta sobre seguridad"
          onChange={event => setLabel(event.target.value)}
        />
      </label>

      <input
        ref={inputRef}
        type="file"
        accept="audio/*"
        multiple
        disabled={upload.isPending}
        onChange={event => void handleFiles(event.target.files)}
      />

      {upload.isPending && <p className="continue__note">Subiendo…</p>}
      {queued > 0 && (
        <p className="voice-actions__ok">
          {queued} {queued === 1 ? 'archivo en cola' : 'archivos en cola'}. Se agregan a la voz en la siguiente corrida
          del pipeline.
        </p>
      )}
      {rejected.length > 0 && (
        <ul className="upload-clips__rejected">
          {rejected.map(reason => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** The API takes raw base64, without the `data:audio/…;base64,` prefix a data URL carries. */
function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('no se pudo leer el archivo'));
    reader.onload = () => {
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });
}
